"use client";

import { useState, useEffect, Suspense } from "react";
import { useUser } from '@/app/context/UserContext';
import { useRouter, useSearchParams } from "next/navigation";
import { refresh } from "next/cache";

function LoginComponent() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showConflictModal, setShowConflictModal] = useState(false);
  const { setUser } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const message = searchParams.get('message');
    if (message === 'logged_out_from_another_device') {
      setError('You have been logged out because this account was logged into from another device.');
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent, force = false) {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, force }),
      });

      if (res.ok) {
        const data = await res.json();
        const user = data.data?.user;

        if (user && user.permissions && user.permissions.length > 0) {
          localStorage.setItem("loginTime", Date.now().toString());
          setUser(user);
          window.location.href = user.permissions[0];
        } else {
          setError("You do not have permission to access any pages.");
        }
      } else {
        const data = await res.json();
        if (res.status === 409) {
          setShowConflictModal(true);
        } else {
          setError(data?.message || "Invalid credentials");
        }
      }
    } catch (err) {
      setError("Network error");
    }
  }

  const handleForceLogin = (e: React.FormEvent) => {
    setShowConflictModal(false);
    handleSubmit(e, true);
  
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
        <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white">Sign in</h1>
        {error && (
          <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800">
            {error}
          </div>
        )}
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor="email"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="block w-full px-3 py-2 text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="block w-full px-3 py-2 text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300"
          >
            Login
          </button>
        </form>
      </div>

      {showConflictModal && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
            <h2 className="text-xl font-bold text-center text-gray-900 dark:text-white">Already Logged In</h2>
            <p className="text-center text-gray-700 dark:text-gray-300">
              This account is already logged in on another device. Do you want to log out from the other device and log in here?
            </p>
            <div className="flex justify-around">
              <button
                onClick={() => setShowConflictModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                No
              </button>
              <button
                onClick={handleForceLogin}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginComponent />
    </Suspense>
  );
}